import { useState } from "react";
import { Link } from "react-router-dom";
import { BUSINESS, SERVICE_AREAS } from "../../mocks/businessData";
import TrustBar from "../home/components/TrustBar";
import AIAnswerBlock from "../home/components/AIAnswerBlock";
import ProcessSection from "../home/components/ProcessSection";
import WhyChooseUs from "../home/components/WhyChooseUs";
import ServiceAreasSection from "../home/components/ServiceAreasSection";
import TestimonialsSection from "../home/components/TestimonialsSection";
import FAQSection from "../home/components/FAQSection";
import QuoteFormSection from "../home/components/QuoteFormSection";
import { useScrollReveal } from "../../hooks/useScrollReveal";

const HERO_BG = "https://public.readdy.ai/ai/img_res/5b8a847bb829d575603cb736032e430e.jpg";

const NJ = SERVICE_AREAS.filter((a) => a.includes("NJ"));
const NY = SERVICE_AREAS.filter((a) => a.includes("NY"));
const OTHER = SERVICE_AREAS.filter((a) => !a.includes("NJ") && !a.includes("NY"));
const TABS = [
  { label: "New Jersey", areas: NJ,    icon: "ri-map-2-fill" },
  { label: "New York",   areas: NY,    icon: "ri-building-fill" },
  { label: "PA & CT",    areas: OTHER, icon: "ri-map-pin-2-fill" },
];

const SERVICE_CITIES = [
  { city: "Newark, NJ",       services: "Drapery · Blind · Carpet Cleaning", icon: "ri-building-2-fill" },
  { city: "Jersey City, NJ",  services: "Drapery · Silhouette · Luminette", icon: "ri-building-fill" },
  { city: "Hoboken, NJ",      services: "Blind · Drapery · Chandelier Cleaning", icon: "ri-home-5-fill" },
  { city: "Manhattan, NY",    services: "Luxury Drapery · Blind · Carpet", icon: "ri-building-4-fill" },
  { city: "Brooklyn, NY",     services: "Drapery · Blind · Area Rug", icon: "ri-home-4-fill" },
  { city: "Philadelphia, PA", services: "Drapery · Blind · Upholstery", icon: "ri-map-pin-fill" },
];

const REGIONS = [
  {
    name: "New Jersey",
    tag: "Primary Coverage",
    desc: "Our home state — we serve all of New Jersey from Belleville, NJ, with no trip charge.",
    icon: "ri-map-2-fill",
    count: NJ.length,
  },
  {
    name: "New York",
    tag: "Metro Coverage",
    desc: "Manhattan, Brooklyn, Queens, The Bronx, Staten Island, and Westchester County.",
    icon: "ri-building-fill",
    count: NY.length,
  },
  {
    name: "Pennsylvania",
    tag: "Regional Coverage",
    desc: "Philadelphia and surrounding Pennsylvania communities.",
    icon: "ri-map-pin-fill",
    count: 1,
  },
  {
    name: "Connecticut",
    tag: "Extended Coverage",
    desc: "All of Connecticut — or simply ship your treatments directly to our Belleville, NJ facility.",
    icon: "ri-map-pin-2-fill",
    count: 1,
  },
];

export default function LocationPage() {
  const [tab, setTab] = useState(0);
  const ref = useScrollReveal();
  const ref2 = useScrollReveal();

  return (
    <main>
      {/* 1 — Hero */}
      <section className="relative pt-44 pb-32 overflow-hidden" style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="absolute inset-0 bg-gradient-to-b from-stone-950/90 via-stone-950/72 to-stone-950/92" />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950/60 to-transparent" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 text-left">
          <div className="hero-badge flex items-center gap-3 mb-8">
            <span className="inline-block w-10 h-px bg-amber-500" />
            <span className="text-amber-400 text-xs tracking-[0.40em] uppercase font-medium">
              Serving 40+ Cities · NJ · NY · PA · CT
            </span>
          </div>
          <h1 className="hero-h1 font-serif font-light text-5xl md:text-7xl lg:text-[5.5rem] text-white mb-6 leading-[1.03] max-w-4xl">
            White-Glove Drapery
            <span className="block italic text-amber-300 mt-1">Cleaning Near You.</span>
          </h1>
          <div className="hero-sub w-20 h-px bg-amber-500 mb-7" />
          <p className="hero-sub text-stone-300 text-base md:text-xl max-w-lg leading-relaxed font-light mb-12">
            Professional drapery, blind &amp; carpet cleaning across New Jersey, New York, Pennsylvania,
            and Connecticut — with free pickup and perfect reinstallation.
          </p>
          <div className="hero-ctas flex flex-col sm:flex-row items-start gap-4">
            <a href="tel:19734891019" className="btn-luxury bg-amber-700 hover:bg-amber-600 text-white text-xs tracking-[0.25em] uppercase font-semibold px-10 py-4 rounded-sm whitespace-nowrap cursor-pointer">
              <i className="ri-phone-fill mr-2" />{BUSINESS.phone}
            </a>
            <a href="#quote-form" onClick={(e) => { e.preventDefault(); document.getElementById("quote-form")?.scrollIntoView({ behavior: "smooth" }); }} className="btn-luxury border border-white/30 hover:border-amber-400/70 bg-white/5 hover:bg-white/10 text-white hover:text-amber-300 text-xs tracking-[0.25em] uppercase font-semibold px-10 py-4 rounded-sm whitespace-nowrap cursor-pointer transition-colors duration-300">
              Check Your Area
            </a>
          </div>
        </div>
      </section>

      {/* 2 — Trust Bar */}
      <TrustBar />

      {/* 3 — Service Coverage Overview */}
      <section className="py-24 bg-white" ref={ref}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="reveal text-center mb-14">
            <p className="text-amber-700 text-xs tracking-[0.40em] uppercase font-semibold mb-4">Coverage</p>
            <h2 className="font-serif font-light text-4xl md:text-5xl text-stone-900 mb-5 leading-tight">
              Expert Cleaning Across{" "}
              <span className="italic text-amber-700">Four States</span>
            </h2>
            <div className="gold-rule mx-auto mb-5" />
            <p className="text-stone-500 text-base max-w-xl mx-auto">
              From Newark to Manhattan — the same white-glove standard in every city we serve.
              Not in our area? Ship your items and we&apos;ll return them perfectly restored.
            </p>
          </div>

          {/* Region cards */}
          <div className="reveal grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-16">
            {REGIONS.map((region, i) => (
              <div key={region.name} className={`reveal reveal-delay-${i + 1} depth-card bg-stone-50 border border-stone-100 hover:border-amber-200 p-7 group cursor-default`}>
                <div className="w-10 h-10 flex items-center justify-center bg-amber-50 border border-amber-100 rounded-sm mb-5 group-hover:bg-amber-700 transition-colors duration-300">
                  <i className={`${region.icon} text-amber-700 group-hover:text-white text-lg transition-colors duration-300`} />
                </div>
                <span className="text-amber-700/60 text-xs tracking-[0.28em] uppercase font-semibold">{region.tag}</span>
                <h3 className="font-serif text-2xl text-stone-900 mt-1 mb-3">{region.name}</h3>
                <p className="text-stone-500 text-sm leading-relaxed">{region.desc}</p>
              </div>
            ))}
          </div>

          {/* Featured city/service combos */}
          <div className="reveal grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-16">
            {SERVICE_CITIES.map((item, i) => (
              <div key={i} className="border border-stone-100 hover:border-amber-200 bg-stone-50 hover:bg-white p-6 transition-all duration-300 rounded-sm group cursor-default service-card-hover">
                <div className="flex items-center gap-2.5 mb-3">
                  <span className="w-8 h-8 flex items-center justify-center bg-amber-50 border border-amber-100 rounded-sm flex-shrink-0">
                    <i className={`${item.icon} text-amber-700 text-sm`} />
                  </span>
                  <h3 className="font-semibold text-stone-900 text-sm group-hover:text-amber-800 transition-colors">{item.city}</h3>
                </div>
                <p className="text-stone-500 text-xs leading-relaxed">{item.services}</p>
              </div>
            ))}
          </div>

          {/* Region tabs */}
          <div className="reveal" ref={ref2}>
            <div className="flex flex-wrap gap-2 mb-8">
              {TABS.map((t, i) => (
                <button key={t.label} onClick={() => setTab(i)}
                  className={`flex items-center gap-1.5 px-5 py-2.5 text-xs tracking-widest uppercase font-semibold rounded-sm transition-all duration-300 cursor-pointer whitespace-nowrap btn-luxury ${tab === i ? "bg-amber-700 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>
                  <i className={`${t.icon} text-sm`} />
                  {t.label} ({t.areas.length})
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
              {TABS[tab].areas.map((area) => (
                <div key={area} className="flex items-center gap-2 p-3 border border-stone-100 hover:border-amber-300 hover:bg-amber-50/40 rounded-sm transition-all duration-200 group cursor-pointer service-card-hover">
                  <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-fill text-amber-600 text-xs group-hover:text-amber-700" />
                  </span>
                  <div>
                    <p className="text-stone-700 text-xs font-medium group-hover:text-amber-900 transition-colors">{area}</p>
                    <p className="text-stone-400 text-xs">Cleaning Service</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Map */}
          <div className="reveal mt-16">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-amber-700 text-xs tracking-[0.40em] uppercase font-semibold mb-2">Google Maps</p>
                <h3 className="font-serif font-light text-3xl text-stone-900">Our Location &amp; Service Territory</h3>
              </div>
              <a
                href={BUSINESS.google}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-luxury hidden sm:flex items-center gap-2 border border-stone-200 hover:border-amber-600 text-stone-600 hover:text-amber-700 text-xs tracking-[0.2em] uppercase font-semibold px-5 py-2.5 rounded-sm transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-star-fill text-amber-500" />
                340+ Reviews
              </a>
            </div>
            <div className="w-full h-96 rounded-sm overflow-hidden border border-stone-200">
              <iframe
                title="The Drapery & Blind Co. — Belleville NJ"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d387191.03606485997!2d-74.30932773785966!3d40.697403441436166!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c25606eba06e03%3A0xe106d3090c6047fd!2sThe%20Drapery%20%26%20Blind%20Co.!5e0!3m2!1sen!2sus!4v1711234567890!5m2!1sen!2sus"
                width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-4 p-5 bg-stone-950 rounded-sm">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-700/20 border border-amber-700/30 rounded-sm flex-shrink-0">
                  <i className="ri-map-pin-fill text-amber-500 text-lg" />
                </div>
                <div>
                  <p className="font-semibold text-white text-sm">The Drapery &amp; Blind Co.</p>
                  <p className="text-stone-400 text-xs">Belleville, NJ · Serving NJ, NY, PA &amp; CT</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href={BUSINESS.phoneHref} className="btn-luxury bg-amber-700 hover:bg-amber-600 text-white text-xs tracking-[0.22em] uppercase font-semibold px-6 py-2.5 rounded-sm whitespace-nowrap cursor-pointer">
                  <i className="ri-phone-fill mr-1.5" />{BUSINESS.phone}
                </a>
                <a href={BUSINESS.google} target="_blank" rel="noopener noreferrer" className="btn-luxury border border-stone-700 hover:border-amber-600 text-stone-300 hover:text-amber-300 text-xs tracking-[0.22em] uppercase font-semibold px-6 py-2.5 rounded-sm whitespace-nowrap cursor-pointer transition-all duration-300">
                  <i className="ri-google-fill mr-1.5" />View on Google
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4 — AI Answer Block */}
      <AIAnswerBlock />
      {/* 5 — Process */}
      <ProcessSection />
      {/* 6 — Why Choose Us */}
      <WhyChooseUs />
      {/* 7 — Service Areas */}
      <ServiceAreasSection />
      {/* 8 — Testimonials */}
      <TestimonialsSection />
      {/* 9 — FAQ */}
      <FAQSection />
      {/* 10 — Final CTA + Form */}
      <QuoteFormSection />
    </main>
  );
}
